import clsx from 'clsx'
import PropTypes from 'prop-types'

import { Reveal } from '../Stage'
import { AUDIO } from './script'

import s from './slides.module.scss'

/*
 * Die zehn Folien. Englisch, weil das Publikum aus ganz Europa kommt — das
 * gilt hier für den Inhalt, nicht für die Bedienung rundherum (die bleibt
 * zweisprachig über i18n). `<Reveal at={n}>` erscheint mit Schritt n, also mit
 * dem Satz `LINES[id, n]` aus script.js.
 */

const img = name => `${AUDIO}/${name}.jpg`

function Kicker({ children }) {
	return <div className={s.kicker}>{children}</div>
}

Kicker.propTypes = { children: PropTypes.node }

function Title() {
	return (
		<div className={s.photoSlide} style={{ backgroundImage: `url(${img('title')})` }}>
			<div className={s.shadeLeft} />
			<div className={s.titleText}>
				<Kicker>Junior Achievement Austria</Kicker>
				<h1 className={s.hero}>
					One platform for the
					<br />
					whole <em>Junior year</em>
				</h1>
				<Reveal at={1} className={s.stats}>
					<div>
						<b>592</b>
						<span>student companies</span>
					</div>
					<div>
						<b>4,966</b>
						<span>students</span>
					</div>
					<div>
						<b>185</b>
						<span>schools</span>
					</div>
					<div>
						<b>9</b>
						<span>federal states</span>
					</div>
				</Reveal>
				<Reveal at={1} className={s.footnote}>
					School year 2025/26
				</Reveal>
			</div>
		</div>
	)
}

const ENTITIES = [
	['🏫', 'Schools'],
	['👩‍🏫', 'Teachers'],
	['🎓', 'Students'],
	['🏢', 'Student companies']
]

function MasterData() {
	return (
		<div className={s.light}>
			<Kicker>01 · Master data</Kicker>
			<h2 className={s.h2}>It starts with clean master data</h2>
			<div className={s.entities}>
				{ENTITIES.map(([icon, label]) => (
					<div key={label} className={s.entity}>
						<span className={s.icon}>{icon}</span>
						<span>{label}</span>
					</div>
				))}
			</div>
			<div className={s.linkLine}>
				<span>each linked to its federal state</span>
			</div>
			<Reveal at={1} className={s.banner}>
				<b>33</b>
				<span>
					competences in <strong>one shared catalogue</strong> — for students, teachers and business experts
				</span>
			</Reveal>
		</div>
	)
}

const FORMS = [
	['JC', 'Junior Company'],
	['JCC', 'Junior Compact Company'],
	['JBC', 'Junior Basic Company']
]

function Forms() {
	return (
		<div className={s.light}>
			<Kicker>02 · Company forms</Kicker>
			<h2 className={s.h2}>Three company forms, side by side</h2>
			<div className={s.forms}>
				{FORMS.map(([code, name]) => (
					<div key={code} className={s.form}>
						<span className={s.code}>{code}</span>
						<b>{name}</b>
						<Reveal at={1} className={s.switches}>
							<span>Steps</span>
							<span>Rules</span>
							<span>Uploads</span>
						</Reveal>
					</div>
				))}
			</div>
			<Reveal at={1} className={s.centralNote}>
				⚙ Switched centrally in the settings — not built per school
			</Reveal>
		</div>
	)
}

function Founding() {
	return (
		<div className={s.split}>
			<div className={s.splitText}>
				<Kicker>03 · Founding</Kicker>
				<h2 className={s.h2}>Students found their company online</h2>
				<ol className={s.flow}>
					<li>
						<b>Business idea</b>
						<span>what we sell and to whom</span>
					</li>
					<li>
						<b>Market &amp; risk analysis</b>
						<span>every risk with a strategy</span>
					</li>
					<Reveal as="li" at={1} className={s.flowAccent}>
						<b>Parental consent</b>
						<span>uploaded in the platform — counts once the teacher has checked it ✓</span>
					</Reveal>
				</ol>
			</div>
			<div className={s.splitImage} style={{ backgroundImage: `url(${img('founding')})` }} />
		</div>
	)
}

function Courses() {
	return (
		<div className={s.dark}>
			<Kicker>04 · Learning built in</Kicker>
			<h2 className={clsx(s.h2, s.onDark)}>Courses inside the platform</h2>
			<div className={s.courses}>
				<div className={s.course}>
					<span className={s.badge}>Skills4Retail</span>
					<b>The business concept in 15 fields</b>
					<div className={s.fields}>
						{Array.from({ length: 15 }, (_, i) => (
							<span key={i} />
						))}
					</div>
					<span className={s.mutedDark}>Guided by a learning path with quizzes</span>
					<Reveal at={1} className={s.score}>
						Every field scored <b>0 – 10</b>
					</Reveal>
				</div>
				<Reveal at={1} className={s.course}>
					<span className={clsx(s.badge, s.badgeOrange)}>AI course</span>
					<b>From TalentsLounge — embedded</b>
					<span className={s.mutedDark}>Students learn about AI without leaving the platform</span>
				</Reveal>
			</div>
		</div>
	)
}

function Supporters() {
	return (
		<div className={s.split}>
			<div className={s.splitImage} style={{ backgroundImage: `url(${img('supporters')})` }} />
			<div className={s.splitText}>
				<Kicker>05 · Supporters</Kicker>
				<h2 className={s.h2}>Supporters, managed in one place</h2>
				<div className={s.roles}>
					<span>Business experts</span>
					<span>Jury members</span>
					<span>Trainers</span>
				</div>
				<Reveal at={1} className={s.chain}>
					<span>Need per federal state</span>
					<i>→</i>
					<span>Supporters reply</span>
				</Reveal>
				<Reveal at={2} className={s.chain}>
					<span>Invitation</span>
					<i>→</i>
					<span>Profile with offer</span>
					<i>→</i>
					<span>Students get in touch</span>
				</Reveal>
			</div>
		</div>
	)
}

function Operations() {
	return (
		<div className={s.light}>
			<Kicker>06 · Running the year</Kicker>
			<h2 className={s.h2}>Invoices, reminders, messages</h2>
			<div className={s.twoCols}>
				<div className={s.panel}>
					<b className={s.panelTitle}>🧾 Invoices &amp; reminders</b>
					<div className={s.timeline}>
						<span className={s.tick}>Invoice issued</span>
						<span className={s.tick}>Reminder</span>
						<span className={clsx(s.tick, s.tickDue)}>Report deadline</span>
					</div>
				</div>
				<Reveal at={1} className={s.panel}>
					<b className={s.panelTitle}>✉ Messages to students &amp; teachers</b>
					<div className={s.channels}>
						<span>In the platform</span>
						<span>By e-mail</span>
					</div>
					<span className={s.muted}>From central templates</span>
				</Reveal>
			</div>
		</div>
	)
}

function Workflow() {
	return (
		<div className={s.dark}>
			<Kicker>07 · Workflow</Kicker>
			<h2 className={clsx(s.h2, s.onDark)}>A workflow system behind it all</h2>
			<div className={s.cards}>
				{[
					['Request', 'New'],
					['Task', 'In progress'],
					['Approval', 'Done']
				].map(([what, status]) => (
					<div key={what} className={s.ticket}>
						<b>{what}</b>
						<span className={s.status}>{status}</span>
						<span className={s.mutedDark}>Owner · Status · History</span>
					</div>
				))}
			</div>
			<Reveal at={1} className={s.log}>
				<span>09:12 · Status changed to “In progress”</span>
				<span>09:40 · Assigned to state administration Vienna</span>
				<span>11:05 · Approved</span>
				<b>Nothing gets lost — every change is logged.</b>
			</Reveal>
		</div>
	)
}

function Certificates() {
	return (
		<div className={s.photoSlide} style={{ backgroundImage: `url(${img('certificates')})` }}>
			<div className={s.shadeBottom} />
			<div className={s.bottomText}>
				<Kicker>08 · Certificates</Kicker>
				<h2 className={clsx(s.h2, s.onDark)}>Certificates straight from the platform</h2>
				<Reveal at={1} className={s.quote}>
					From the first record to the certificate: <em>one system, no spreadsheets.</em>
				</Reveal>
			</div>
		</div>
	)
}

function Closing() {
	return (
		<div className={s.photoSlide} style={{ backgroundImage: `url(${img('closing')})` }}>
			<div className={s.shadeLeft} />
			<div className={clsx(s.titleText, s.narrow)}>
				<Kicker>Junior Achievement Austria</Kicker>
				<h1 className={s.hero}>
					Let&apos;s <em>talk.</em>
				</h1>
				<p className={s.lead}>
					We would love to share what we have learned — with every JA organisation in Europe.
				</p>
				<span className={s.url}>www.junior.cc</span>
			</div>
		</div>
	)
}

const SLIDES = {
	title: Title,
	masterData: MasterData,
	forms: Forms,
	founding: Founding,
	courses: Courses,
	supporters: Supporters,
	operations: Operations,
	workflow: Workflow,
	certificates: Certificates,
	closing: Closing
}

export default function Slides({ id }) {
	const Slide = SLIDES[id]
	return Slide ? <Slide /> : null
}

Slides.propTypes = { id: PropTypes.string.isRequired }

/** Für den Test: Jede Folie im Skript braucht eine Darstellung. */
export const SLIDE_IDS = Object.keys(SLIDES)
